<template>
    <div class="earn-vaults">
        <!-- Filters & View Toggle -->
        <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div class="d-flex gap-2 flex-wrap align-items-center">
                <select v-model="selectedProtocol" class="form-select-sm" style="min-width: 140px;">
                    <option value="">All Protocols</option>
                    <option v-for="p in protocols" :key="p" :value="p">{{ capitalize(p) }}</option>
                </select>
                <select v-model="selectedToken" class="form-select-sm" style="min-width: 120px;">
                    <option value="">All Tokens</option>
                    <option v-for="t in tokens" :key="t" :value="t">{{ t }}</option>
                </select>
                <select v-model="sortBy" class="form-select-sm" style="min-width: 120px;">
                    <option value="apy">Sort: APY</option>
                    <option value="tvl">Sort: TVL</option>
                    <option value="token">Sort: Token</option>
                </select>
            </div>
            <div class="analytics-tabs">
                <button class="tab-btn" :class="{ active: viewMode === 'grid' }" @click="viewMode = 'grid'">
                    <i class="fas fa-th-large"></i> Grid
                </button>
                <button class="tab-btn" :class="{ active: viewMode === 'table' }" @click="viewMode = 'table'">
                    <i class="fas fa-list"></i> Table
                </button>
            </div>
        </div>

        <!-- Your Positions (when wallet connected) -->
        <div v-if="hasPositions" class="compass-card compass-card--accent mb-4">
            <h5 class="mb-3" style="font-weight: 600;">Your Earn Positions</h5>
            <div class="row">
                <div v-for="pos in positions" :key="pos.vaultId" class="col-md-4 col-sm-6 mb-3">
                    <div class="compass-stat-card compass-stat-card--compact compass-stat-card--positive">
                        <div class="stat-label">{{ pos.token }} &middot; {{ capitalize(pos.protocol) }}</div>
                        <div class="stat-value font-mono-num">{{ formatAmount(pos.depositedAmount) }} {{ pos.token }}</div>
                        <div class="stat-unit">{{ formatApy(pos.apy) }} APY</div>
                        <button class="compass-btn compass-btn--sm compass-btn--outline mt-2"
                                @click="openWithdraw(pos)">
                            Withdraw
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- SSR slot content: Blade HTML rendered for SEO, removed once Vue mounts -->
        <div v-if="showSeoContent" ref="seoContent">
            <slot></slot>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-purple" role="status"></div>
            <p class="mt-2" style="color: #6b7280;">Loading vaults...</p>
        </div>

        <!-- Grid View -->
        <div v-else-if="viewMode === 'grid'" class="row">
            <div v-for="vault in filteredVaults" :key="vault.vault_address || vault.id"
                 class="col-lg-4 col-md-6 mb-3">
                <div class="compass-card h-100 vault-card" role="button" tabindex="0"
                     @click="openDeposit(vault)" @keydown.enter="openDeposit(vault)" @keydown.space.prevent="openDeposit(vault)">
                    <div class="d-flex align-items-center gap-3">
                        <!-- Token icon -->
                        <div class="vault-icon-wrap">
                            <img v-if="vault.token_logo"
                                 :src="vault.token_logo"
                                 :alt="vault.token_symbol"
                                 class="vault-token-icon"
                                 @error="onIconError($event, vault)"
                            >
                            <span v-else class="vault-token-fallback">
                                {{ (vault.token_symbol || '?').slice(0, 3) }}
                            </span>
                        </div>

                        <!-- Token name + strategy -->
                        <div class="flex-grow-1" style="min-width: 0;">
                            <div class="vault-token-name">{{ vault.token_symbol }}</div>
                            <div class="vault-strategy-name text-truncate">{{ strategyLabel(vault) }}</div>
                        </div>

                        <!-- APY -->
                        <div class="text-end flex-shrink-0">
                            <div class="vault-apy font-mono-num">{{ formatApy(vault.apy) }}</div>
                            <div class="vault-apy-label">APY</div>
                        </div>
                    </div>

                    <!-- Footer: protocol badge + TVL + deposit -->
                    <div class="d-flex justify-content-between align-items-center mt-3 pt-2"
                         style="border-top: 1px solid rgba(139, 92, 246, 0.1);">
                        <div class="d-flex align-items-center gap-2">
                            <span class="compass-badge compass-badge--outline vault-protocol-badge">
                                {{ capitalize(vault.protocol) }}
                            </span>
                            <span class="vault-tvl font-mono-num">{{ formatTvl(vault) }}</span>
                        </div>
                        <button class="compass-btn compass-btn--sm compass-btn--primary"
                                :disabled="!vault.deposits_enabled"
                                @click.stop="openDeposit(vault)">
                            {{ vault.deposits_enabled ? 'Deposit' : 'Paused' }}
                        </button>
                    </div>
                </div>
            </div>
            <div v-if="!filteredVaults.length" class="col-12 text-center py-4" style="color: #6b7280;">
                No vaults match your filters.
            </div>
        </div>

        <!-- Table View -->
        <div v-else class="table-mobile-scroll">
            <table class="compass-table">
                <thead>
                    <tr>
                        <th>Token</th>
                        <th>Strategy</th>
                        <th>Protocol</th>
                        <th class="text-end" style="cursor: pointer;" @click="sortBy = 'apy'">
                            APY <i v-if="sortBy === 'apy'" class="fas fa-sort-down"></i>
                        </th>
                        <th class="text-end" style="cursor: pointer;" @click="sortBy = 'tvl'">
                            TVL <i v-if="sortBy === 'tvl'" class="fas fa-sort-down"></i>
                        </th>
                        <th class="text-end">Action</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="vault in filteredVaults" :key="vault.vault_address || vault.id">
                        <td>
                            <div class="d-flex align-items-center gap-2">
                                <img v-if="vault.token_logo"
                                     :src="vault.token_logo"
                                     :alt="vault.token_symbol"
                                     class="table-token-icon"
                                     @error="onIconError($event, vault)"
                                >
                                <span v-else class="table-token-fallback">
                                    {{ (vault.token_symbol || '?').slice(0, 2) }}
                                </span>
                                <span style="font-weight: 600;">{{ vault.token_symbol }}</span>
                            </div>
                        </td>
                        <td style="color: #6b7280; font-size: 0.85rem;">{{ strategyLabel(vault) }}</td>
                        <td>
                            <span class="compass-badge compass-badge--outline vault-protocol-badge">
                                {{ capitalize(vault.protocol) }}
                            </span>
                        </td>
                        <td class="td-num" style="color: var(--compass-success); font-weight: 600;">
                            {{ formatApy(vault.apy) }}
                        </td>
                        <td class="td-num">{{ formatTvl(vault) }}</td>
                        <td class="text-end">
                            <button class="compass-btn compass-btn--sm compass-btn--primary"
                                    :disabled="!vault.deposits_enabled"
                                    @click="openDeposit(vault)">
                                {{ vault.deposits_enabled ? 'Deposit' : 'Paused' }}
                            </button>
                        </td>
                    </tr>
                    <tr v-if="!filteredVaults.length">
                        <td colspan="6" class="text-center" style="color: #6b7280;">No vaults match your filters.</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Deposit/Withdraw Modal -->
        <earn-vault-deposit
            v-if="showModal"
            :vault="selectedVault"
            :mode="modalMode"
            :position="selectedPosition"
            @close="showModal = false"
            @success="onTxSuccess"
        ></earn-vault-deposit>
    </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useProtocolRates } from '../composables/useProtocolRates';
import { useSolSdk } from '../composables/useSolSdk';
import { useLendFormatters } from '../composables/useLendFormatters';

export default {
    name: 'EarnVaults',
    props: {
        initialVaults: { type: Array, default: () => [] },
        protocols: { type: Array, default: () => [] },
        tokens: { type: Array, default: () => [] },
    },
    setup(props) {
        const { fetchEarnVaults } = useProtocolRates();
        const { getSdk } = useSolSdk();
        const { capitalize, formatApy, formatAmount } = useLendFormatters();

        const showSeoContent = ref(true);
        const seoContent = ref(null);
        const vaults = ref(props.initialVaults);
        const loading = ref(false);
        const viewMode = ref('grid');
        const selectedProtocol = ref('');
        const selectedToken = ref('');
        const sortBy = ref('apy');

        // Modal state
        const showModal = ref(false);
        const selectedVault = ref(null);
        const modalMode = ref('deposit');
        const selectedPosition = ref(null);

        // Positions state
        const positions = ref([]);
        const hasPositions = computed(() => positions.value.length > 0);

        const filteredVaults = computed(() => {
            let result = [...vaults.value];

            if (selectedProtocol.value) {
                result = result.filter(v => v.protocol === selectedProtocol.value);
            }
            if (selectedToken.value) {
                result = result.filter(v => v.token_symbol === selectedToken.value);
            }

            result.sort((a, b) => {
                if (sortBy.value === 'apy') return (b.apy || 0) - (a.apy || 0);
                if (sortBy.value === 'tvl') return (b.tvl_usd || b.tvl_token || 0) - (a.tvl_usd || a.tvl_token || 0);
                if (sortBy.value === 'token') return (a.token_symbol || '').localeCompare(b.token_symbol || '');
                return 0;
            });

            return result;
        });

        /**
         * Display the strategy/vault name, stripping redundant protocol+token info.
         * e.g. "Loopscale SOL" → "Loopscale" (token is already shown separately)
         * e.g. "Main Market SOL" → "Main Market"
         * e.g. "JLP Vault" → "JLP Vault"
         */
        function strategyLabel(vault) {
            const name = vault.vault_name || '';
            const token = vault.token_symbol || '';
            const proto = capitalize(vault.protocol);

            // If name is empty, just show protocol
            if (!name) return proto + ' Vault';

            // Remove trailing token symbol from name (e.g. "Loopscale SOL" → "Loopscale")
            let label = name;
            if (label.endsWith(' ' + token)) {
                label = label.slice(0, -(token.length + 1)).trim();
            }

            // If what remains is just the protocol name, return it
            if (!label || label.toLowerCase() === proto.toLowerCase()) {
                return proto;
            }

            return label;
        }

        function onIconError(event, vault) {
            event.target.style.display = 'none';
            vault.token_logo = null; // trigger fallback via v-if
        }

        function formatTvl(vault) {
            const tvl = vault.tvl_usd;
            if (!tvl) {
                const tokens = vault.tvl_token || 0;
                return tokens >= 1000
                    ? Number(tokens).toLocaleString(undefined, { maximumFractionDigits: 0 }) + ' ' + (vault.token_symbol || '')
                    : Number(tokens).toFixed(2) + ' ' + (vault.token_symbol || '');
            }
            if (tvl >= 1_000_000) return '$' + (tvl / 1_000_000).toFixed(1) + 'M';
            if (tvl >= 1_000) return '$' + (tvl / 1_000).toFixed(1) + 'K';
            return '$' + Number(tvl).toFixed(0);
        }

        function openDeposit(vault) {
            selectedVault.value = vault;
            modalMode.value = 'deposit';
            selectedPosition.value = null;
            showModal.value = true;
        }

        function openWithdraw(position) {
            selectedVault.value = {
                vault_address: position.vaultId,
                vault_name: position.vaultName,
                token_symbol: position.token,
                token_mint: position.mint,
                token_logo: null, // positions don't carry logo
                protocol: position.protocol,
                apy: position.apy,
            };
            modalMode.value = 'withdraw';
            selectedPosition.value = position;
            showModal.value = true;
        }

        function onTxSuccess() {
            showModal.value = false;
            refreshVaults();
            loadPositions();
        }

        async function refreshVaults() {
            loading.value = true;
            try {
                const fresh = await fetchEarnVaults();
                if (fresh && fresh.length) {
                    vaults.value = fresh;
                }
            } catch (e) {
                console.error('[EarnVaults] refresh failed:', e);
            } finally {
                loading.value = false;
            }
        }

        async function loadPositions() {
            const wallet = window.wallet;
            const pubkey = wallet?.publicKey?.value;
            if (!pubkey) {
                positions.value = [];
                return;
            }

            try {
                const sdk = await getSdk();
                const result = await sdk.earn.getPositions(pubkey.toBase58());
                positions.value = result || [];
            } catch (e) {
                console.error('[EarnVaults] Failed to load positions:', e);
                positions.value = [];
            }
        }

        onMounted(() => {
            // Remove SSR slot content — Vue interactive UI takes over
            showSeoContent.value = false;
            refreshVaults();

            // Load positions if wallet already connected
            if (window.wallet?.publicKey?.value) {
                loadPositions();
            }

            // Watch for wallet connect/disconnect — registered inside onMounted
            // so window.wallet is guaranteed to be initialized by the adapter
            const checkWallet = () => {
                if (window.wallet?.publicKey) {
                    watch(() => window.wallet.publicKey.value, (newVal) => {
                        if (newVal) loadPositions();
                        else positions.value = [];
                    });
                } else {
                    // Wallet adapter not yet initialized — retry once after a tick
                    setTimeout(() => {
                        if (window.wallet?.publicKey) {
                            watch(() => window.wallet.publicKey.value, (newVal) => {
                                if (newVal) loadPositions();
                                else positions.value = [];
                            });
                        }
                    }, 500);
                }
            };
            checkWallet();
        });

        return {
            showSeoContent,
            seoContent,
            vaults,
            loading,
            viewMode,
            selectedProtocol,
            selectedToken,
            sortBy,
            filteredVaults,
            showModal,
            selectedVault,
            modalMode,
            selectedPosition,
            positions,
            hasPositions,
            capitalize,
            strategyLabel,
            onIconError,
            formatApy,
            formatTvl,
            formatAmount,
            openDeposit,
            openWithdraw,
            onTxSuccess,
        };
    },
};
</script>

<style scoped>
.vault-card {
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.vault-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(139, 92, 246, 0.12);
}

.vault-icon-wrap {
    flex-shrink: 0;
}
.vault-token-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
}
.vault-token-fallback {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--compass-purple), var(--compass-pink));
    color: #fff;
    font-size: 0.7rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    text-transform: uppercase;
}

.table-token-icon {
    width: 24px;
    height: 24px;
    border-radius: 50%;
}
.table-token-fallback {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--compass-purple), var(--compass-pink));
    color: #fff;
    font-size: 0.55rem;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-transform: uppercase;
}

.vault-token-name {
    font-weight: 700;
    font-size: 1.1rem;
    line-height: 1.2;
}
.vault-strategy-name {
    font-size: 0.78rem;
    color: #6b7280;
    line-height: 1.2;
}

.vault-apy {
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--compass-success);
}
.vault-apy-label {
    font-size: 0.65rem;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.vault-tvl {
    font-size: 0.8rem;
    color: #6b7280;
}

.vault-protocol-badge {
    font-size: 0.65rem;
    padding: 2px 6px;
}
</style>
